import { Button, Divider, Icon, Link, MenuItem } from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans } from "react-i18next";
import styled from "styled-components";

import {
    ActionPermissionsObject,
    IWorkspaceResourceFolder,
    isTeamResourceAllTeamMembersFolderOrSubfolder,
    LibraryRecentSearchScope,
    PermissionActionType,
    WorkspacePlanTier,
    WorkspaceResourceFolderType,
    WorkspaceResourceType
} from "common/interfaces";

import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import { PresentationListHeader } from "js/editor/PresentationLibrary/PresentationListHeader";
import { SearchAndAdd, SearchAndAddFilterOption } from "js/editor/PresentationLibrary/components/SearchAndAdd";
import {
    buildResourcePaneBreadcrumbData,
    PresentationLibraryPaneBreadcrumb,
} from "js/editor/PresentationLibrary/components/PresentationLibraryPaneBreadcrumb";
import { Layout, PaneBody, PaneContent, ResourcePaneSideBar, SearchBarSort, teamResourceGridProps } from "js/react/views/SharedResources/Components";
import BulkActionBar, { BulkActionButton } from "js/react/views/SharedResources/Components/BulkActionBar";
import { LabeledCheckbox } from "js/react/views/SharedResources/Components/LabeledCheckbox";
import { WorkspaceResourceFolderHeaderActions } from "js/react/views/SharedResources/Components/WorkspaceResourceFolderMenu";

import getObjectHash from "common/utils/getObjectHash";
import Banner from "js/Components/Banner";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { getStaticUrl } from "js/config";
import SharedSlidesPaneController, { SharedSlidesItem, SharedSlidesPaneControllerState } from "js/controllers/SharedSlidesPaneController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { Slide } from "js/core/models/slide";
import ux from "js/core/services/userExperiences";
import { trackActivity } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import { ShowConfirmationDialog, ShowDialog, ShowDialogAsync, ShowErrorDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { Gap20, Gap5 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { NoMatchNotice, NoTeamSlideNotice, SharedSlideFolderNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import { themeColors } from "js/react/sharedStyles";
import { UIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { MultiSelectThumbnailGrid } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { SharedSlideThumbnail } from "js/react/views/SharedResources/Components/SharedSlideThumbnail";
import { selectedClass, SideBarItem } from "js/react/views/SharedResources/Components/WorkspaceResourcesSidebarHelper";
import { useFolderDragItems } from "js/react/views/SharedResources/hooks/useFolderDragItems";
import { folderDropResourceFromWorkspaceResource, moveResourcesToFolder } from "js/react/views/SharedResources/utils/folderDropHelpers";
import { removeResourcesFromFolder } from "js/react/views/SharedResources/utils/removeResourcesFromFolder";
import TeamSlideDialog from "js/react/views/TeamResources/dialogs/TeamSlideDialog";
import AddTagsDialog from "js/react/components/Dialogs/AddTagsDialog";
import CreateSharedSlideDialog from "js/react/views/UserOptions/dialogs/CreateSharedSlideDialog";
import { _ } from "js/vendor";
import { TemplatesListHeaderRow } from "js/editor/PresentationLibrary/libraryTemplatesPaneShared";

const logger = getLogger(LogGroup.TEAMS);
const checkIcon = getStaticUrl("/images/auth/done-check.gif");

const ResultsContainer = styled(UIPaneResultsContainer)`
    margin-top: 0;
    padding: 0;
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;
`;

const AddSlideContainer = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 100%;
    background: ${themeColors.lightGray};
    padding-top: 20px;
    z-index: 1000;
`;

const AddSlideButton = styled(Button)`
    background: #2EB4E5 !important;
    color: white !important;
    min-width: 150px !important;
    margin-bottom: 15px !important;
    padding: 10px 20px !important;

    &:disabled {
        background: white !important;
        color: #A9A9A9 !important;
    }
`;

const StatsContainer = styled.div`
    background: #f1f1f1;
    width: 100%;
    padding: 15px;
    font-weight: 600;
    .MuiIcon-root {
        margin-right: 10px;
    }
    & > div + div {
        margin-top: 10px;
    }
`;

const SearchControls = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

export const ALL_SLIDES_RESOURCE_TYPES = [WorkspaceResourceType.SLIDE, WorkspaceResourceType.SLIDE_TEMPLATE];
export const SHARED_SLIDES_RESOURCE_TYPES = [WorkspaceResourceType.SLIDE];
export const SLIDE_TEMPLATES_RESOURCE_TYPES = [WorkspaceResourceType.SLIDE_TEMPLATE];

const CONTENT_FILTER_ALL = "all_content";
const TAG_FILTER_ALL = "__all__";

type SharedSlidesPaneProps = WorkspaceControllerState & SharedSlidesPaneControllerState & {
    resourceTypes: WorkspaceResourceType[];
    isManageLibrary: boolean;
    showUpvote?: boolean;
    onSelectFolder?: (resourceTypes: WorkspaceResourceType[], folderId?: string) => void;
    onSelected?: (items: Pick<SharedSlidesItem, "id" | "libraryItemId" | "name">[]) => void;
    folderId?: string;
};

const contentFilterOptions: SearchAndAddFilterOption[] = [
    { field: CONTENT_FILTER_ALL, name: t("All Content") },
    { field: "resource:metadata:name", name: t("Title") },
    { field: "resource:metadata:description", name: t("Description") },
    { field: "slide:text", name: t("Slide Content") },
    { field: "resource:tagsText", name: t("Tags") },
    { field: "slide:templateTitle", name: t("Template") },
];

const sortOptions: SearchBarSort[] = [{
    field: "resource.metadata.name", label: t("Name"), direction: "asc", icon: "sort_by_alpha",
}, {
    field: "resource.metadata.ratedByUsersCount", label: t("Rating"), direction: "desc", icon: "star",
}, {
    field: "resource.metadata.usedInPresentationsCount", label: t("Popularity"), direction: "desc", icon: "trending_up",
}, {
    field: "resource.createdAt", label: t("Created At"), direction: "desc", icon: "calendar_month",
}, {
    field: "resource.modifiedAt", label: t("Modified At"), direction: "desc", icon: "edit_calendar",
}];

const ListHeaderBreadcrumbGroup = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 10px;
`;

const ListHeaderControls = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
`;

export function SharedSlidesPane({ resourceTypes: initialResourceTypes, tags, fetching, data, isManageLibrary, showUpvote, resourceFolders, onSelectFolder, onSelected, folderId }: SharedSlidesPaneProps) {
    const [isInitializedForCurrentMount, setIsInitializedForCurrentMount] = useState(false);
    const [hasEverHadResults, setHasEverHadResults] = useState(false);
    const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState<SearchBarSort>({
        field: "resource.metadata.name",
        direction: "asc",
    });
    const [selectedContent, setSelectedContent] = useState(CONTENT_FILTER_ALL);
    const [selectedTag, setSelectedTag] = useState(TAG_FILTER_ALL);
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(folderId ?? null);
    const [searchInFolder, setSearchInFolder] = useState(() => Boolean(folderId));
    const selectedFolder = resourceFolders.find(folder => folder.id === selectedFolderId);
    const parentFolder = resourceFolders.find(folder => folder.id === selectedFolder?.parentFolderId);
    const canRemoveFromSelectedFolder = !isTeamResourceAllTeamMembersFolderOrSubfolder(selectedFolder, parentFolder);
    const [showHidden, setShowHidden] = useState(true);
    const [selectedItems, setSelectedItems] = useState<SharedSlidesItem[]>([]);

    const clearSelection = useCallback(() => {
        setSelectedItems([]);
    }, []);
    const {
        draggedItems,
        handleStartDragItems,
        handleDropDragItems,
        clearDragItems,
        getActiveDragItems,
    } = useFolderDragItems<SharedSlidesItem>(item => folderDropResourceFromWorkspaceResource(item.resource));
    const [resourceTypes, setResourceTypes] = useState<WorkspaceResourceType[]>(initialResourceTypes);

    const searchResults = SharedSlidesPaneController.initialized ? SharedSlidesPaneController.legacyData : [];

    useEffect(() => {
        setResourceTypes(initialResourceTypes);
    }, [initialResourceTypes]);

    useEffect(() => {
        setSelectedFolderId(folderId ?? null);
        setSearchInFolder(Boolean(folderId));
    }, [folderId]);

    // Refetches clear data, so data.length alone can't tell an empty workspace from one that is
    // reloading. Once slides have been seen, the workspace is known not to be empty.
    useEffect(() => {
        if (data.length > 0) {
            setHasEverHadResults(true);
        }
    }, [data.length]);

    // initialize() resolves while the first page is still loading, so the pane is only done
    // loading once that first fetch settles.
    useEffect(() => {
        if (isInitializedForCurrentMount && !fetching) {
            setHasCompletedInitialLoad(true);
        }
    }, [isInitializedForCurrentMount, fetching]);

    useEffect(() => {
        ux.addSlideToPresentation.update({ subtype: ux.Subtype[ux.Type.ADD_SLIDES_TO_PRESENTATION].ADD_SHARED_SLIDES_AND_COPY_TEAM_TEMPLATES });
    }, []);

    const controllerSort = [{
        field: sort.field,
        direction: sort.direction,
    }];

    const getControllerFilters = (nextResourceTypes: WorkspaceResourceType[]) => {
        const filters = [];

        if (nextResourceTypes.length > 0 || selectedFolderId) {
            filters.push({
                field: "resource.resourceType",
                operator: "in",
                // If a folder is selected, we always show all slides and slide templates in that folder
                value: selectedFolderId ? ALL_SLIDES_RESOURCE_TYPES : nextResourceTypes,
            });
        }

        if (selectedTag !== TAG_FILTER_ALL) {
            filters.push({
                field: "resource.tags",
                operator: "eq",
                value: selectedTag,
            });
        }

        if (selectedFolderId) {
            filters.push({
                field: "resource.folderIds",
                operator: "eq",
                value: selectedFolderId,
            });
        }

        if (!isManageLibrary || !showHidden) {
            filters.push({
                field: "resource.metadata.isDisabled",
                operator: "ne",
                value: true
            });
        }

        return filters;
    };

    const controllerFilters = useMemo(() => getControllerFilters(resourceTypes), [resourceTypes, selectedTag, selectedFolderId, isManageLibrary, showHidden]);

    const controllerSearch = useMemo(() => ({
        query,
        fields: selectedContent === CONTENT_FILTER_ALL ? undefined : [selectedContent],
    }), [query, selectedContent]);

    const tagFilterOptions = useMemo<SearchAndAddFilterOption[]>(() => [
        { field: TAG_FILTER_ALL, name: t("All Tags") },
        ...tags.map(tag => ({
            field: tag.tag,
            name: tag.tag,
            count: tag.count,
        })),
    ], [tags]);

    const existingTags = useMemo(() => tags.map(tag => tag.tag), [tags]);

    useEffect(() => {
        let isMounted = true;

        void SharedSlidesPaneController.initialize({
            workspaceId: WorkspaceController.workspaceId,
            resourceTypes: ALL_SLIDES_RESOURCE_TYPES,
            sort: controllerSort,
            filters: getControllerFilters(initialResourceTypes),
            search: controllerSearch,
        }).then(() => {
            if (isMounted) {
                setIsInitializedForCurrentMount(true);
            }
        });

        return () => {
            isMounted = false;
            SharedSlidesPaneController.reset();
        };
    }, []);

    const prevSearchStateRef = useRef({
        query,
        selectedContent,
        selectedTag,
        selectedFolderId,
        resourceTypes,
        sort,
        showHidden,
    });

    useEffect(() => {
        if (!SharedSlidesPaneController.initialized) {
            return;
        }

        const prev = prevSearchStateRef.current;
        const hasFilterChanged = !_.isEqual(prev.resourceTypes, resourceTypes) ||
            prev.selectedTag !== selectedTag ||
            prev.selectedFolderId !== selectedFolderId ||
            prev.showHidden !== showHidden;
        const hasSearchChanged = prev.query !== query || prev.selectedContent !== selectedContent;

        if (!_.isEqual(prev.sort, sort)) {
            SharedSlidesPaneController.setSort(controllerSort);
        }
        if (hasFilterChanged) {
            SharedSlidesPaneController.setFilters(controllerFilters);
        }
        if (hasSearchChanged) {
            SharedSlidesPaneController.setSearch(controllerSearch);
        }
        if (hasFilterChanged || hasSearchChanged) {
            clearSelection();
        }

        prevSearchStateRef.current = { query, selectedContent, selectedTag, selectedFolderId, resourceTypes, sort, showHidden };
    }, [query, selectedContent, selectedTag, selectedFolderId, resourceTypes, showHidden, getObjectHash(sort), clearSelection]);

    useEffect(() => {
        setSelectedItems(selectedItems => {
            const itemIds = new Set(searchResults.map(item => item.id));
            const filteredItems = selectedItems.filter(item => itemIds.has(item.id));
            return filteredItems.length !== selectedItems.length ? filteredItems : selectedItems;
        });
    }, [getObjectHash(searchResults)]);

    const showTeamSlideDialog = async (item: { id: string }) => {
        await ShowDialogAsync(TeamSlideDialog, {
            slideId: item.id,
        });
    };

    const copySlide = async (
        itemModel: SharedSlidesItem,
        updateProps: Partial<Pick<SharedSlidesItem, "name" | "tags" | "description" | "isDisabled" | "isTemplate">> = {}
    ) => {
        try {
            await withProgressDialog({
                title: t("Copying slide..."),
                action: async () => {
                    const {
                        name,
                        id,
                        tags,
                        description,
                        isDisabled,
                        isTemplate,
                    } = { ...itemModel, ...updateProps };

                    const slide = new Slide({ id });
                    await slide.load();

                    const slideModel = _.cloneDeep(slide.attributes);
                    delete slideModel.id;
                    delete slideModel.libraryItemId;
                    delete slideModel.thumbnails;
                    delete slideModel.presentationId;

                    const newSlide = new Slide(slideModel, {
                        userId: app.userId,
                        autoSync: false,
                    });
                    await newSlide.load();

                    const resource = {
                        resourceId: newSlide.id,
                        resourceType: isTemplate ? WorkspaceResourceType.SLIDE_TEMPLATE : WorkspaceResourceType.SLIDE,
                        tags: tags ?? [],
                        folderIds: [],
                        metadata: {
                            name,
                            description,
                            isDisabled,
                            contentModifiedAt: newSlide.get("modifiedAt"),
                            usedByUsers: [],
                            usedInPresentations: [],
                            usedInPublicPresentations: [],
                            ratedByUsers: [],
                            createdBy: app.user.id,
                            modifiedBy: app.user.id,
                        },
                    };

                    await WorkspaceController.createWorkspaceResource({ resource, defaultPermissions: "shared" });

                    if (selectedFolderId) {
                        await WorkspaceController.updateWorkspaceResourceFolderResources({
                            folderId: selectedFolderId,
                            update: {
                                add: [{
                                    resourceId: resource.resourceId,
                                    resourceType: resource.resourceType,
                                    permissions: {
                                        read: { object: true, permissions: true },
                                        write: { object: false, permissions: false },
                                    },
                                }],
                            },
                        });
                    }
                },
            });

            return true;
        } catch (err) {
            logger.error(err, "[SharedSlidesPane] copySlide failed");

            ShowErrorDialog({
                error: t("An error occurred while copying the slide."),
                message: (err as Error).message,
            });

            return false;
        }
    };

    const handleEditSlide = async (item: { id: string, resource: { resourceType: WorkspaceResourceType } }) => {
        AppController.showSlideEditor({ slideId: item.id, resourceType: item.resource.resourceType });
    };

    const handleCopySlide = async (item: SharedSlidesItem) => {
        if (item.isTemplate) {
            const didCopy = await copySlide(item, {
                isTemplate: false,
                name: t("Copy of {{name}}", { name: item.name }),
            });

            if (didCopy) {
                ShowMessageDialog({
                    dialogTitleProps: {
                        style: { padding: "10px 34px" },
                    },
                    buttonOptions: {
                        color: "blue"
                    },
                    title: t("A copy of this slide is now available to your team as a finished, uneditable Shared Slide."),
                    header: (<img src={checkIcon} width={75} height={75} />),
                });
            }
            return;
        }

        const didCopy = await copySlide(item, {
            isTemplate: true,
            name: t("Copy of {{name}}", { name: item.name }),
            isDisabled: false,
        });

        if (didCopy) {
            ShowMessageDialog({
                dialogTitleProps: {
                    style: { padding: "10px 34px" },
                },
                buttonOptions: {
                    color: "blue"
                },
                title: t("A copy of this slide is now available to your team as an editable Slide Template."),
                header: (<img src={checkIcon} width={75} height={75} />),
            });
        }
    };

    const handleDuplicateSlide = async (item: SharedSlidesItem) => {
        try {
            const slide = new Slide({ id: item.id });
            await slide.load();

            await ShowDialogAsync(CreateSharedSlideDialog, {
                slide,
                presentation: null,
                resourceType: item.isTemplate ? WorkspaceResourceType.SLIDE_TEMPLATE : WorkspaceResourceType.SLIDE,
                folderId: selectedFolderId,
                forceCreateNewSlide: true,
                initialName: t("Copy of {{name}}", { name: item.name }),
                initialDescription: item.description ?? "",
                initialTags: item.tags,
                initialIsDisabled: item.isDisabled,
                dialogTitle: t("Duplicate this team slide"),
                okButtonLabel: t("Duplicate"),
            });
        } catch (err) {
            logger.error(err, "[SharedSlidesPane] duplicate slide failed", { id: item.id });

            ShowErrorDialog({
                error: t("An error occurred while duplicating the slide."),
                message: (err as Error).message,
            });
        }
    };

    const handleRemoveFromFolder = async (item: {
        id: string;
        resource: { resourceId: string; resourceType: WorkspaceResourceType };
    }) => {
        if (!canRemoveFromSelectedFolder || !selectedFolderId) {
            return;
        }

        const items = selectedItems.length > 0 && selectedItems.some(selected => selected.id === item.id)
            ? selectedItems
            : [item];
        const resources = items.map(selectedItem => ({
            resourceId: selectedItem.resource.resourceId,
            resourceType: selectedItem.resource.resourceType,
        }));

        await removeResourcesFromFolder({
            folderId: selectedFolderId,
            resources,
            resourceLabel: items.length === 1 ? t("slide") : t("slides"),
        });
    };

    const handleDeleteSlides = useCallback(async (items: SharedSlidesItem[]) => {
        const visibleItems = items.filter(item => searchResults.some(result => result.id === item.id));
        if (!visibleItems.length) {
            clearSelection();
            return;
        }

        const isSingle = visibleItems.length === 1;

        if (await ShowConfirmationDialog({
            title: isSingle
                ? t("Permanently delete this Team Slide?")
                : t("Permanently delete {{count}} selected Team Slides?", { count: visibleItems.length }),
            message: t("This cannot be undone. Existing slides will remain unchanged. Instances of deleted Shared Slides will no longer receive updates."),
            buttonOptions: { variant: "outlined", style: { color: "#FF4500", borderColor: "transparent" } },
            okButtonLabel: t("DELETE"),
        })) {
            try {
                await withProgressDialog({
                    title: isSingle
                        ? t("Deleting Team Slide...")
                        : t("Deleting {{count}} slides...", { count: visibleItems.length }),
                    action: () => Promise.all(visibleItems.map(item => WorkspaceController.deleteWorkspaceResource(
                        {
                            resourceId: item.resource.resourceId,
                            resourceType: item.resource.resourceType,
                        },
                        { trackActivity: "SharedSlideLibrary" }
                    ))),
                });
            } catch (err) {
                logger.error(err, "[SharedSlidesPane] delete failed", {
                    resources: visibleItems.map(item => ({
                        resourceId: item.resource.resourceId,
                        resourceType: item.resource.resourceType,
                    })),
                });

                ShowErrorDialog({
                    error: t("An error occurred while deleting the Team Slide."),
                    message: (
                        <>
                            <p>
                                <strong>{t("Error")}:</strong> {(err as Error).message}
                            </p>
                            <p>{t("We apologize for the inconvenience. Please contact us at support@beautiful.ai.")}</p>
                        </>
                    ),
                });
            }
            clearSelection();
        }
    }, [clearSelection, searchResults]);

    const hasPublished = selectedItems.some(item => !item.isDisabled);
    const hasUnpublished = selectedItems.some(item => item.isDisabled);

    const handleBulkPublish = useCallback(async (publish: boolean, targetItems?: SharedSlidesItem[]) => {
        const rawItems = targetItems || selectedItems;
        const items = rawItems.filter(item => searchResults.some(result => result.id === item.id));
        if (!items.length) {
            return;
        }

        const isSingle = items.length === 1;

        if (publish) {
            const confirmed = await ShowConfirmationDialog({
                title: isSingle
                    ? t("Are you sure you want to publish this slide?")
                    : t("Are you sure you want to publish {{count}} selected slides?", { count: items.length }),
                message: isSingle
                    ? t("Publishing this Shared Slide allows it to be used in future presentations.")
                    : t("Publishing these {{count}} Shared Slides allows them to be used in future presentations.", { count: items.length }),
                okButtonLabel: isSingle ? t("Publish Slide") : t("Publish {{count}} Slides", { count: items.length }),
            });

            if (!confirmed) {
                return;
            }
        }

        const itemsToUpdate = items.filter(item => (publish ? item.isDisabled : !item.isDisabled));
        if (!itemsToUpdate.length) {
            clearSelection();
            return;
        }

        await withProgressDialog({
            title: publish
                ? (isSingle ? t("Publishing slide...") : t("Publishing {{count}} slides...", { count: items.length }))
                : (isSingle ? t("Unpublishing slide...") : t("Unpublishing {{count}} slides...", { count: items.length })),
            action: async () => {
                await WorkspaceController.updateWorkspaceResources(itemsToUpdate.map(({ resource }) => ({
                    resourceId: resource.resourceId,
                    resourceType: resource.resourceType,
                    metadata: { isDisabled: !publish },
                })));
            },
        });
        clearSelection();
    }, [clearSelection, selectedItems]);

    const handleBulkAddTags = useCallback(async (targetItems?: SharedSlidesItem[]) => {
        const rawItems = targetItems || selectedItems;
        const items = rawItems.filter(item => searchResults.some(result => result.id === item.id));
        if (!items.length) {
            return;
        }

        const count = items.length;
        const subTitle = count === 1
            ? t("Add tags to this resource to help your team find it faster. Existing tags will not be removed.")
            : t("Add tags to {{count}} selected resources to help your team find them faster. Existing tags will not be removed.", { count });

        ShowDialog(AddTagsDialog, {
            activeTags: [],
            existingTags,
            subTitle,
            acceptCallback: async (newTags: string[]) => {
                if (!newTags.length) {
                    return;
                }
                await withProgressDialog({
                    title: t("Updating tags..."),
                    action: async () => {
                        await WorkspaceController.updateWorkspaceResources(items.map(item => {
                            const existing = (item.tags || []).map(tag => _.trim(tag)).filter(Boolean);
                            return {
                                resourceId: item.resource.resourceId,
                                resourceType: item.resource.resourceType,
                                tags: _.uniqBy([...existing, ...newTags], tag => tag.toLowerCase()),
                            };
                        }));
                    },
                });
                clearSelection();
            },
        });
    }, [clearSelection, existingTags, selectedItems]);

    const handleRemoveMultipleFromFolder = useCallback(async (items: SharedSlidesItem[]) => {
        if (!canRemoveFromSelectedFolder || !selectedFolderId || !items.length) {
            return;
        }
        const resources = items.map(item => ({
            resourceId: item.resource.resourceId,
            resourceType: item.resource.resourceType,
        }));
        await removeResourcesFromFolder({
            folderId: selectedFolderId,
            resources,
            resourceLabel: items.length === 1 ? t("slide") : t("slides"),
        });
        clearSelection();
    }, [canRemoveFromSelectedFolder, clearSelection, selectedFolderId]);

    const handleNavigateToFolder = useCallback(({ folderId: nextFolderId, subFolderId }: { folderId: string, subFolderId?: string }) => {
        const nextSelectedFolderId = subFolderId ?? nextFolderId;
        setSelectedFolderId(nextSelectedFolderId);
        setSearchInFolder(true);
        setQuery("");
        clearSelection();
        onSelectFolder?.(resourceTypes, nextSelectedFolderId);
    }, [clearSelection, onSelectFolder, resourceTypes]);

    const handleFolderDrop = useCallback(async (folder: IWorkspaceResourceFolder) => {
        const items = getActiveDragItems();
        if (!items.length) {
            return;
        }

        if (folder.id !== selectedFolderId) {
            setSelectedFolderId(folder.id);
            setSearchInFolder(true);
            setQuery("");
            onSelectFolder?.(resourceTypes, folder.id);
        }

        clearDragItems();

        await moveResourcesToFolder({
            folder,
            resources: items,
            progressTitle: t("Moving slide to folder..."),
            progressTitlePlural: count => t("Moving {{count}} slides to folder...", { count }),
            errorMessage: t("An error occurred while moving slides to the folder."),
            logTag: "SharedSlidesPane",
        });
    }, [clearDragItems, getActiveDragItems, onSelectFolder, resourceTypes, selectedFolderId]);

    const handleThumbnailVisibilityChange = (isVisible: boolean, item: { resource: { id: string } }) => {
        if (!isVisible || !SharedSlidesPaneController.canLoadMore) {
            return;
        }

        const index = data.findIndex(({ resource }) => resource.id === item.resource.id);
        if (index === data.length - 1) {
            SharedSlidesPaneController.loadMore();
        }
    };

    const handleAddItem = async ({ createInFolder = false }: { createInFolder?: boolean } = {}) => {
        const resourceType = _.isEqual(resourceTypes, SHARED_SLIDES_RESOURCE_TYPES)
            ? WorkspaceResourceType.SLIDE
            : WorkspaceResourceType.SLIDE_TEMPLATE;
        const folderIdForCreate = createInFolder ? selectedFolderId ?? undefined : undefined;

        // Top-level create should leave the folder view so the new slide lands at root.
        if (!createInFolder && selectedFolderId) {
            setSelectedFolderId(null);
            setSearchInFolder(false);
            onSelectFolder?.(ALL_SLIDES_RESOURCE_TYPES, null);
        }

        AppController.showSlideEditor({
            resourceType,
            slideId: undefined,
            folderId: folderIdForCreate,
        });
    };

    const handleAddSelectedSlides = (items: Pick<SharedSlidesItem, "id" | "libraryItemId" | "name">[] = selectedItems) => {
        if (!onSelected) {
            return;
        }

        const analyticsProps = {
            ...ds.selection.presentation.getAnalytics(),
            slide_ids: items.map(item => item.id),
        };
        trackActivity("Presentation", "LibrarySlideAdded", null, null, analyticsProps);

        onSelected(items);
    };

    const handleSearch = (nextQuery: string) => {
        setQuery(nextQuery);
        if (searchInFolder || !nextQuery.trim() || !selectedFolderId) {
            return;
        }

        // Chip off + typing = global search. Chip remove alone keeps the folder path.
        setSearchInFolder(false);
        setResourceTypes(ALL_SLIDES_RESOURCE_TYPES);
        setSelectedFolderId(null);
        clearSelection();
        onSelectFolder?.(ALL_SLIDES_RESOURCE_TYPES, null);
    };

    const selectedFolderName = selectedFolderId
        ? resourceFolders.find(folder => folder.id === selectedFolderId)?.name ?? null
        : null;

    const handleSort = (nextSort: SearchBarSort) => {
        setSort(nextSort);
    };

    const handleContentFilterChange = (event: { target: { value: string } }) => {
        setSelectedContent(event.target.value);
        clearSelection();
    };

    const handleSelectTag = (event: { target: { value: string } }) => {
        setSelectedTag(event.target.value);
        clearSelection();
    };

    const handleSelectResourceType = useCallback((nextResourceTypes: WorkspaceResourceType[]) => {
        const isAllSlidesView = _.isEqual(nextResourceTypes, ALL_SLIDES_RESOURCE_TYPES);
        const isAlreadySelected = isAllSlidesView &&
            _.isEqual(resourceTypes, ALL_SLIDES_RESOURCE_TYPES) &&
            selectedFolderId === null &&
            !folderId;

        if (isAlreadySelected) {
            return;
        }

        setResourceTypes(nextResourceTypes);
        setSelectedFolderId(null);
        setSearchInFolder(false);
        if (!isAllSlidesView) {
            setQuery("");
        }
        clearSelection();
        onSelectFolder?.(nextResourceTypes, null);
    }, [clearSelection, folderId, onSelectFolder, resourceTypes, selectedFolderId]);

    const hasSlideFolders = resourceFolders.some(
        folder => folder.resourceTypes.some(resourceType => ALL_SLIDES_RESOURCE_TYPES.includes(resourceType)) &&
            !folder.parentFolderId
    );

    const handleSelectSidebarFolder = useCallback((nextFolderId: string | null) => {
        setSelectedFolderId(nextFolderId);
        setSearchInFolder(Boolean(nextFolderId));
        setQuery("");
        setResourceTypes(ALL_SLIDES_RESOURCE_TYPES);
        clearSelection();
        onSelectFolder?.(ALL_SLIDES_RESOURCE_TYPES, nextFolderId ?? undefined);
    }, [clearSelection, onSelectFolder]);

    const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.MANAGE
    );
    const isAllSlidesView = _.isEqual(resourceTypes, ALL_SLIDES_RESOURCE_TYPES);

    const shouldHideTopLevelSideBar = data.length === 0 &&
        !hasEverHadResults &&
        !query &&
        selectedTag === TAG_FILTER_ALL &&
        !selectedFolderId &&
        !folderId &&
        isAllSlidesView &&
        !hasSlideFolders;
    // Sidebar and header both wait for the first fetch to settle so the loading spinner stands
    // alone and the pane chrome appears in one step instead of piece by piece.
    const shouldRenderSideBar = hasCompletedInitialLoad && !shouldHideTopLevelSideBar;
    const shouldHideTopLevelHeader = !hasCompletedInitialLoad;

    const sideBarCategoryNav = useMemo(() => (
        <>
            <SideBarItem
                className={selectedClass(_.isEqual(resourceTypes, ALL_SLIDES_RESOURCE_TYPES) && selectedFolderId === null)}
                onClick={() => handleSelectResourceType(ALL_SLIDES_RESOURCE_TYPES)}
                id="all-slides"
            >
                <Icon>select_all</Icon>
                {t("All slides")}
            </SideBarItem>
            <SideBarItem
                className={selectedClass(_.isEqual(resourceTypes, SHARED_SLIDES_RESOURCE_TYPES) && selectedFolderId === null)}
                onClick={() => handleSelectResourceType(SHARED_SLIDES_RESOURCE_TYPES)}
                id="shared-slides"
            >
                <Icon>link</Icon>
                {t("Shared Slides")}
            </SideBarItem>
            <SideBarItem
                className={selectedClass(_.isEqual(resourceTypes, SLIDE_TEMPLATES_RESOURCE_TYPES) && selectedFolderId === null)}
                onClick={() => handleSelectResourceType(SLIDE_TEMPLATES_RESOURCE_TYPES)}
                id="slide-templates"
            >
                <Icon>edit_note</Icon>
                {t("Slide Templates")}
            </SideBarItem>
        </>
    ), [handleSelectResourceType, resourceTypes, selectedFolderId]);

    const renderContextMenu = useCallback((selection: SharedSlidesItem[]) => {
        if (selection.length === 0) {
            return null;
        }

        if (selection.length > 1) {
            const hasSelectionUnpublished = selection.some(item => item.isDisabled);
            const hasSelectionPublished = selection.some(item => !item.isDisabled);
            return [
                <MenuItem key="add-tags" divider onClick={() => void handleBulkAddTags(selection)}>
                    <Icon>label</Icon>{t("Add tags…")}
                </MenuItem>,
                ...(hasSelectionUnpublished ? [
                    <MenuItem key="publish" onClick={() => void handleBulkPublish(true, selection)}>
                        <Icon>cloud_upload</Icon>{t("Publish")}
                    </MenuItem>
                ] : []),
                ...(hasSelectionPublished ? [
                    <MenuItem key="unpublish" onClick={() => void handleBulkPublish(false, selection)}>
                        <Icon>unpublished</Icon>{t("Unpublish")}
                    </MenuItem>
                ] : []),
                ...(hasSelectionUnpublished || hasSelectionPublished ? [
                    <Divider key="visibility-divider" />
                ] : []),
                ...(canRemoveFromSelectedFolder ? [
                    <MenuItem key="remove-from-folder" divider onClick={() => handleRemoveMultipleFromFolder(selection)}>
                        <Icon>remove_circle</Icon>{t("Remove from Folder")}
                    </MenuItem>
                ] : []),
                <MenuItem key="delete" onClick={() => handleDeleteSlides(selection)}>
                    <Icon>delete</Icon>{t("Delete")}
                </MenuItem>,
            ];
        }

        const item = selection[0];
        return [
            ...(canUpgradeToManageSharedResources ? [
                <MenuItem key="edit" disabled><UpgradeIcon /><Gap5 />{t("Edit Slide…")}</MenuItem>,
                <MenuItem key="properties" disabled><UpgradeIcon /><Gap5 />{t("Properties…")}</MenuItem>,
                <MenuItem key="add-tags" disabled divider><UpgradeIcon /><Gap5 />{t("Add tags…")}</MenuItem>,
                <MenuItem key="duplicate" disabled divider><UpgradeIcon /><Gap5 />{t("Duplicate…")}</MenuItem>,
                item.isDisabled
                    ? <MenuItem key="publish" disabled divider><UpgradeIcon /><Gap5 />{t("Publish")}</MenuItem>
                    : <MenuItem key="unpublish" disabled divider><UpgradeIcon /><Gap5 />{t("Unpublish")}</MenuItem>,
            ] : [
                <MenuItem key="edit" onClick={() => handleEditSlide(item)}><Icon>edit</Icon>{t("Edit Slide…")}</MenuItem>,
                <MenuItem key="properties" onClick={() => showTeamSlideDialog(item)}><Icon>label</Icon>{t("Properties…")}</MenuItem>,
                <MenuItem key="add-tags" divider onClick={() => void handleBulkAddTags([item])}><Icon>label</Icon>{t("Add tags…")}</MenuItem>,
                <MenuItem key="duplicate" divider onClick={() => handleDuplicateSlide(item)}><Icon>file_copy</Icon>{t("Duplicate…")}</MenuItem>,
                item.isDisabled
                    ? <MenuItem key="publish" divider onClick={() => void handleBulkPublish(true, [item])}><Icon>cloud_upload</Icon>{t("Publish")}</MenuItem>
                    : <MenuItem key="unpublish" divider onClick={() => void handleBulkPublish(false, [item])}><Icon>unpublished</Icon>{t("Unpublish")}</MenuItem>,
            ]),
            ...(canRemoveFromSelectedFolder ? [
                <MenuItem key="remove-from-folder" divider onClick={() => handleRemoveFromFolder(item)}>
                    <Icon>remove_circle</Icon>{t("Remove from Folder")}
                </MenuItem>
            ] : []),
            <MenuItem key="delete" onClick={() => handleDeleteSlides([item])}><Icon>delete</Icon>{t("Delete")}</MenuItem>,
        ];
    }, [
        canRemoveFromSelectedFolder,
        canUpgradeToManageSharedResources,
        handleBulkAddTags,
        handleBulkPublish,
        handleDeleteSlides,
        handleDuplicateSlide,
        handleEditSlide,
        handleRemoveFromFolder,
        handleRemoveMultipleFromFolder,
        selectedFolderId,
        showTeamSlideDialog,
    ]);

    const renderSlides = () => {
        const isLoadingMore = fetching && searchResults.length > 0;
        const isLoadingInitial = fetching && searchResults.length === 0;

        if (isLoadingInitial || !SharedSlidesPaneController.initialized) {
            return <Spinner />;
        }

        if (!searchResults.length) {
            if (!query) {
                if (selectedFolderId) {
                    return (<SharedSlideFolderNotice hideMessage={!isManageLibrary} />);
                }

                if (selectedTag === TAG_FILTER_ALL && !selectedFolderId) {
                    if (_.isEqual(resourceTypes, SHARED_SLIDES_RESOURCE_TYPES)) {
                        return (<SharedSlideFolderNotice
                            title={t("You don’t have any Shared Slides yet.")}
                            message={t("Create a slide once, and your team can use it infinitely. Updates will apply anywhere the slide is used.")}
                        />);
                    }
                    if (_.isEqual(resourceTypes, SLIDE_TEMPLATES_RESOURCE_TYPES)) {
                        return (<SharedSlideFolderNotice
                            title={t("You don’t have any Slide Templates yet.")}
                            message={t("Give your team a head start with a custom library of slide templates.")}
                        />);
                    }

                    return <NoTeamSlideNotice />;
                }
            }
            return <NoMatchNotice />;
        }

        return (
            <MultiSelectThumbnailGrid
                {...teamResourceGridProps}
                items={searchResults}
                selection={selectedItems.map(item => item.id)}
                allowMultiSelect
                allowDragging={isManageLibrary && canUseSharedResources}
                thumbnailClass={SharedSlideThumbnail}
                isLoadingMore={isLoadingMore}
                thumbnailProps={{
                    isManageLibrary,
                    showUpvote,
                    canUpgradeToManageSharedResources,
                    folderId: selectedFolderId,
                    onEditSlide: handleEditSlide,
                    onDeleteSlide: item => handleDeleteSlides([item]),
                    onRemoveFromFolder: canRemoveFromSelectedFolder ? handleRemoveFromFolder : undefined,
                    onCopySlide: handleCopySlide,
                    onDuplicateSlide: handleDuplicateSlide,
                    onVisibleChange: handleThumbnailVisibilityChange,
                    renderContextMenuItems: renderContextMenu,
                    resourceFolders,
                    onNavigateToFolder: handleNavigateToFolder,
                }}
                onItemSelected={setSelectedItems}
                onStartDragItems={handleStartDragItems}
                onDropDragItems={handleDropDragItems}
                renderContextMenuItems={isManageLibrary ? renderContextMenu : undefined}
                onDoubleClick={canUpgradeToManageSharedResources ? undefined : (slide => {
                    if (isManageLibrary) {
                        showTeamSlideDialog(slide);
                    } else {
                        handleAddSelectedSlides([slide]);
                    }
                })}
            />
        );
    };

    const title = _.isEqual(resourceTypes, SHARED_SLIDES_RESOURCE_TYPES)
        ? t("Shared Slides")
        : _.isEqual(resourceTypes, SLIDE_TEMPLATES_RESOURCE_TYPES)
            ? t("Slide Templates")
            : t("Slides");

    const breadcrumb = useMemo(() => buildResourcePaneBreadcrumbData({
        rootLabel: title,
        selectedFolderId,
        resourceFolders,
        onSelectFolder: handleSelectSidebarFolder,
    }), [title, selectedFolderId, resourceFolders, handleSelectSidebarFolder]);

    return (
        <Layout>
            {canUseSharedResources && !shouldHideTopLevelHeader && (<>
                <SearchControls>
                    <SearchAndAdd
                        onSearch={handleSearch}
                        searchValue={query}
                        searchPlaceholder={t("Search slides...")}
                        recentSearchScope={LibraryRecentSearchScope.TEAM_SLIDES}
                        contentFilterOptions={contentFilterOptions}
                        selectedContentFilter={selectedContent}
                        onContentFilterChange={handleContentFilterChange}
                        tagFilterOptions={tagFilterOptions}
                        selectedTagFilter={selectedTag}
                        onTagFilterChange={handleSelectTag}
                        selectedFolderName={selectedFolderName}
                        searchInFolder={searchInFolder}
                        onSearchInFolderChange={setSearchInFolder}
                        actions={isManageLibrary ? [{
                            label: t("New Slide"),
                            onClick: () => handleAddItem(),
                            variant: "primary",
                        }] : []}
                    />
                </SearchControls>
            </>)}

            {canUpgradeToManageSharedResources && (
                <FlexBox center fillWidth>
                    <Banner
                        headerText={t("Unlock Slides")}
                        bodyText={t("Upgrade to a Team plan to customize a searchable library of reusable templates and Shared Slides that stay synced across every deck.")}
                        buttonLabel={t("Upgrade")}
                        onButtonClick={() => ShowDialog(ChangePlanDialog,
                            {
                                featuredPlan: WorkspacePlanTier.TEAM,
                                analytics: { cta: "team_slides" }
                            })}
                    />
                </FlexBox>
            )}

            <PaneBody>
                <ResourcePaneSideBar
                    visible={shouldRenderSideBar}
                    readOnly={!isManageLibrary}
                    resourceTypes={ALL_SLIDES_RESOURCE_TYPES}
                    folderType={WorkspaceResourceFolderType.SHARED}
                    resourceFolders={resourceFolders}
                    selectedFolderId={selectedFolderId}
                    draggedItems={draggedItems}
                    onFolderDrop={handleFolderDrop}
                    onSelectFolder={handleSelectSidebarFolder}
                >
                    {sideBarCategoryNav}
                </ResourcePaneSideBar>

                <PaneContent>
                    {!shouldHideTopLevelHeader && (
                        <TemplatesListHeaderRow>
                            <ListHeaderBreadcrumbGroup>
                                <PresentationLibraryPaneBreadcrumb {...breadcrumb} />
                                {isManageLibrary && selectedFolderId && (
                                    <WorkspaceResourceFolderHeaderActions
                                        folderId={selectedFolderId}
                                        resourceTypes={ALL_SLIDES_RESOURCE_TYPES}
                                        resourceFolders={resourceFolders}
                                        onSelectFolder={handleSelectSidebarFolder}
                                        onCreateItem={() => handleAddItem({ createInFolder: true })}
                                        showCreateButton
                                    />
                                )}
                            </ListHeaderBreadcrumbGroup>
                            <ListHeaderControls>
                                {isManageLibrary && (
                                    <LabeledCheckbox
                                        checked={showHidden}
                                        label={t("Show hidden slides")}
                                        onChange={setShowHidden}
                                        hasData={hasEverHadResults}
                                    />
                                )}
                                {canUseSharedResources && (
                                    <PresentationListHeader
                                        sortOptions={sortOptions}
                                        sort={sort}
                                        searchQuery={query}
                                        onSetSort={handleSort}
                                        showViewTypeToggle={false}
                                    />
                                )}
                            </ListHeaderControls>
                        </TemplatesListHeaderRow>
                    )}
                    <ResultsContainer>
                        {renderSlides()}
                    </ResultsContainer>
                    {!isManageLibrary && !fetching && SharedSlidesPaneController.legacyData.length > 0 &&
                        <AddSlideContainer>
                            <AddSlideButton disabled={!selectedItems.length} onClick={() => handleAddSelectedSlides()}>
                                {t("Add Selected Slides")}
                            </AddSlideButton>
                        </AddSlideContainer>
                    }
                </PaneContent>
                {isManageLibrary && (
                    <BulkActionBar
                        selectedCount={selectedItems.length}
                        onDelete={() => handleDeleteSlides(selectedItems)}
                        onClearSelection={clearSelection}
                    >
                        <BulkActionButton
                            data-testid="bulk-action-add-tags"
                            icon="label"
                            onClick={() => handleBulkAddTags()}
                        >
                            {t("Add Tags…")}
                        </BulkActionButton>

                        {hasUnpublished && (
                            <BulkActionButton
                                data-testid="bulk-action-publish"
                                icon="check_circle"
                                onClick={() => handleBulkPublish(true)}
                            >
                                {t("Publish")}
                            </BulkActionButton>
                        )}

                        {hasPublished && (
                            <BulkActionButton
                                data-testid="bulk-action-unpublish"
                                icon="unpublished"
                                onClick={() => handleBulkPublish(false)}
                            >
                                {t("Unpublish")}
                            </BulkActionButton>
                        )}
                    </BulkActionBar>
                )}
            </PaneBody>
        </Layout>
    );
}

export default WorkspaceController.withInitializedState(SharedSlidesPaneController.withState(SharedSlidesPane));
